"use client";

/**
 * Page furniture the site's pages share: the CTA pair and a band's left rail. The page
 * prototypes stay on the concept board (concept/groups/pages.tsx).
 */
import type { ReactNode } from "react";

import { DISPLAY, EDITORIAL_ACTION_CLS, LABEL_CLS } from "@/components/editorial";

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";

/** Editorial CTA pair — real links (cross-page anchors work everywhere). */
export function CtaPair({ onDark = false }: { onDark?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a
        href="#waitlist"
        className={`${EDITORIAL_ACTION_CLS} ${
          onDark
            ? "bg-[var(--theme-ground)] text-[var(--theme-ink)]"
            : "bg-[var(--theme-dark)] text-[var(--theme-ground)]"
        }`}
      >
        Discuss your first workflow
      </a>
      <a
        href="/#how-it-works"
        className={`${EDITORIAL_ACTION_CLS} border ${
          onDark
            ? `border-[color-mix(in_srgb,var(--theme-ground)_36%,transparent)] ${GINK_82}`
            : `border-[var(--rule)] ${INK_80}`
        }`}
      >
        How it works
      </a>
    </div>
  );
}

/** A band's left rail: number, head, plain-words body. */
export function BandRail({
  index,
  head,
  children,
  headingLevel = "h3",
}: {
  index?: string;
  /** Rich content allows hand-drawn marks on a word (concept SKT). */
  head: ReactNode;
  children: ReactNode;
  headingLevel?: "h2" | "h3";
}) {
  const Heading = headingLevel;
  return (
    <div className="max-w-[38rem]">
      {index ? <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>{index}</p> : null}
      <Heading
        style={DISPLAY}
        className={`${INK_88} ${index ? "mt-3" : ""} max-w-[22ch] text-[1.5rem] leading-[1.16] font-semibold tracking-[-0.02em] text-balance sm:text-[1.9rem]`}
      >
        {head}
      </Heading>
      <p className={`mt-3 max-w-[52ch] ${INK_72} text-[0.95rem] leading-[1.65]`}>
        {children}
      </p>
    </div>
  );
}
